import { useFetch } from 'hooks';
import { ERROR_MESSAGES } from 'modules/sign-up/steps/Abn/useAbnUtils';
import { useRef } from 'react';
import {
  ApiOkResponse,
  MappedError,
  readBodySafe,
  throwApiError,
  defaultErrorMapper,
  errorContains,
  createFieldError,
  ApiResponse,
} from 'types/common/ApiHelpers';

import {
  CheckEligibilityResponse,
  AbnLookUpResponse,
  CheckAbnExistResponse,
  CreateApplicationResponse,
} from 'types/responses/sign-up';
import {
  ApplicationFormOption,
  ApplicationFormOptionsMap,
} from 'types/responses/sign-up/ApplicationFormOptionsResponse';
import { logError } from 'utils';
import { PageAction } from 'enums';

export interface SignUpFunctions {
  abortRequests: () => void;

  createMerchantApplicationSignUpInfoAsync: (
    payload: Record<string, any>,
    recaptchaToken?: string
  ) => Promise<ApiOkResponse<CreateApplicationResponse>>;

  createUserAsync: (
    applicationId: string,
    password: string
  ) => Promise<ApiOkResponse>;

  patchMerchantApplicationSignUpInfoAsync: (
    applicationId: string,
    payload: Record<string, any>
  ) => Promise<ApiOkResponse>;

  resendVerificationEmailAsync: (
    applicationId: string
  ) => Promise<ApiOkResponse>;

  getApplicationFormOptionsAsync: () => Promise<ApplicationFormOptionsMap>;
  checkEligibilityAsync: (
    applicationId: string
  ) => Promise<ApiOkResponse<CheckEligibilityResponse>>;
  checkExistingApplicationOrMerchantByAbn: (
    abn: string
  ) => Promise<ApiResponse<CheckAbnExistResponse> | null>;
  abnLookUp: (
    searchText: string
  ) => Promise<ApiResponse<AbnLookUpResponse> | null>;
  getApplicationFormOptions: () => Promise<ApiResponse<
    ApplicationFormOption[]
  > | null>;
  mapServerError: (err: any) => MappedError;
}

export const useSignUpFunctions = (): SignUpFunctions => {
  const { get, post, patch } = useFetch();
  const abortControllerRef = useRef(new AbortController());

  function getAbortSignal(): AbortSignal {
    return abortControllerRef.current.signal;
  }

  function abortRequests(): void {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
  }

  const createMerchantApplicationSignUpInfoAsync = async (
    payload: Record<string, any>,
    recaptchaToken?: string
  ): Promise<ApiOkResponse<CreateApplicationResponse>> => {
    const additionalHeaders = recaptchaToken
      ? { ReCaptchaResponse: recaptchaToken }
      : undefined;

    const res = await post(
      '/MerchantApplications',
      payload,
      getAbortSignal(),
      undefined,
      additionalHeaders
    );
    const body = await readBodySafe(res);

    if (res.ok) {
      return {
        success: true,
        ...body,
      } as ApiOkResponse<CreateApplicationResponse>;
    }

    throwApiError(res, body);
  };

  const createUserAsync = async (
    applicationId: string,
    password: string
  ): Promise<ApiOkResponse> => {
    const res = await post(
      `/MerchantApplications/${applicationId}/users`,
      {
        applicationId,
        password,
      },
      getAbortSignal()
    );

    const body = await readBodySafe(res);

    if (res.ok) {
      return {
        success: true,
        ...body,
      } as ApiOkResponse;
    }

    throwApiError(res, body);
  };

  const patchMerchantApplicationSignUpInfoAsync = async (
    applicationId: string,
    payload: Record<string, any>
  ): Promise<ApiOkResponse> => {
    const res = await patch(
      `/MerchantApplications/${applicationId}`,
      payload,
      getAbortSignal()
    );
    const body = await readBodySafe(res);

    if (res.ok) {
      return {
        success: true,
        ...body,
      } as ApiOkResponse;
    }

    throwApiError(res, body);
  };

  const resendVerificationEmailAsync = async (
    applicationId: string
  ): Promise<ApiOkResponse> => {
    const res = await post(
      `/MerchantApplications/${applicationId}/resend-verification-email`,
      {},
      getAbortSignal()
    );

    const body = await readBodySafe(res);

    if (res.ok) {
      return {
        success: true,
        ...body,
      } as ApiOkResponse;
    }

    throwApiError(res, body);
  };

  const getApplicationFormOptionsAsync =
    async (): Promise<ApplicationFormOptionsMap> => {
      try {
        const res = await get(
          '/merchantapplications/application-form-options',
          getAbortSignal()
        );
        const body: ApplicationFormOption[] = await res.json();

        return body.reduce((map, option) => {
          map[option.questionType] = option.candidateAnswers;
          return map;
        }, {} as ApplicationFormOptionsMap);
      } catch (err) {
        logError(PageAction.merchantOnboardingGetFormOptions, err);
        return {};
      }
    };

  const checkEligibilityAsync = async (
    applicationId: string
  ): Promise<ApiOkResponse<CheckEligibilityResponse>> => {
    const res = await patch(
      `/merchantapplications/${applicationId}/check-eligibility`,
      {},
      getAbortSignal()
    );
    const body = await readBodySafe(res);

    if (res.ok) {
      return {
        success: true,
        ...body,
      } as ApiOkResponse<CheckEligibilityResponse>;
    }

    throwApiError(res, body);
  };

  const checkExistingApplicationOrMerchantByAbn = async (
    abn: string
  ): Promise<ApiResponse<CheckAbnExistResponse> | null> => {
    const res = await get(
      `/MerchantApplications/check-abn-exists/?abn=${abn}`,
      getAbortSignal()
    );
    const body = await readBodySafe(res);

    if (res.ok) {
      return {
        success: true,
        ...body,
      } as ApiOkResponse<CheckAbnExistResponse>;
    }

    throwApiError(res, body);
  };

  const abnLookUp = async (
    searchText: string
  ): Promise<ApiResponse<AbnLookUpResponse> | null> => {
    const res = await get(
      `/AbnLookUp/?SearchText=${searchText}`,
      getAbortSignal()
    );
    const body = await readBodySafe(res);

    if (res.ok) {
      return {
        success: true,
        ...body,
      } as ApiOkResponse<AbnLookUpResponse>;
    }

    throwApiError(res, body);
  };

  const getApplicationFormOptions = async (): Promise<ApiResponse<
    ApplicationFormOption[]
  > | null> => {
    const res = await get(
      `/MerchantApplications/application-form-options`,
      getAbortSignal()
    );
    const body = await readBodySafe(res);

    if (res.ok) {
      return {
        success: true,
        ...body,
      } as ApiOkResponse<ApplicationFormOption[]>;
    }

    throwApiError(res, body);
  };

  const mapServerError = (err: any): MappedError => {
    if (
      errorContains(err, 'MerchantApplicationExistsException') ||
      errorContains(err, 'UserExistsException')
    ) {
      return createFieldError(
        'businessEmail',
        ERROR_MESSAGES.EMAIL_ALREADY_EXISTS
      );
    }

    if (errorContains(err, 'AbnAlreadyExistsException')) {
      return createFieldError('abn', ERROR_MESSAGES.ABN_ALREADY_EXISTS);
    }

    return defaultErrorMapper(err);
  };

  return {
    abortRequests,
    createMerchantApplicationSignUpInfoAsync,
    createUserAsync,
    patchMerchantApplicationSignUpInfoAsync,
    resendVerificationEmailAsync,
    getApplicationFormOptionsAsync,
    checkEligibilityAsync,
    checkExistingApplicationOrMerchantByAbn,
    abnLookUp,
    getApplicationFormOptions,
    mapServerError,
  };
};
